import type { RuleListPatch } from '@atlas/ui-domain'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import type { Rule } from '@/domain/rules.types'
import { useCan } from '@/lib/capabilities'
import { rulesApi } from '@/services/rules.service'

/** The rules manager's page for one rule, where its scope, severity and element types are edited. */
const ruleHref = (ruleId: string) => `/rules/${ruleId}`

/** The card's patch already matches `Rule`'s field names, so this is a straight pick. */
const toRulePatch = (patch: RuleListPatch): Partial<Rule> => ({
	...(patch.statement !== undefined ? { statement: patch.statement } : {}),
	...(patch.checkDefinition !== undefined ? { checkDefinition: patch.checkDefinition } : {}),
})

/**
 * Editing a rule from wherever it was resolved, plus whether the current user may.
 *
 * Shared by every surface that presents resolved rules, so the permission rule and the
 * patch narrowing exist once: a second copy of either is how the two would drift into
 * disagreeing about who may edit what.
 *
 * `canEdit` is `canConfigure`, not `canWork`: a rule constrains what everyone else may
 * produce, so writing one is administration rather than editorial work. This mirrors the
 * roles the API accepts for `PATCH /rules/{id}`, and the API remains the real guard.
 *
 * `ruleHref`/`openRule` are here rather than at each surface so the link to a rule's own
 * page is written once: in-place editing covers two fields, that page covers the rest.
 *
 * Optimistic: a rule is global, so an edit made on any surface patches every cached list
 * that carries that rule (the plain list and every `['rules', 'applicable', …]` resolution) -
 * react-query matches by key PREFIX, and both start with `['rules']`. Rolls back on error,
 * reconciles with the server on settle.
 */
export function useRuleEditing() {
	const { canConfigure } = useCan()
	const queryClient = useQueryClient()
	const navigate = useNavigate()

	const update = useMutation({
		mutationFn: ({ ruleId, patch }: { ruleId: string; patch: RuleListPatch }) =>
			rulesApi.update(ruleId, toRulePatch(patch)),
		onMutate: async ({ ruleId, patch }) => {
			await queryClient.cancelQueries({ queryKey: ['rules'] })
			const snapshots = queryClient.getQueriesData<Array<{ id: string }>>({ queryKey: ['rules'] })
			for (const [key, rules] of snapshots) {
				if (!rules) continue
				queryClient.setQueryData(
					key,
					rules.map((rule) => (rule.id === ruleId ? { ...rule, ...toRulePatch(patch) } : rule)),
				)
			}
			return { snapshots }
		},
		onError: (_err, _vars, ctx) => {
			for (const [key, data] of ctx?.snapshots ?? []) queryClient.setQueryData(key, data)
		},
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: ['rules'] })
		},
	})

	return {
		canEdit: canConfigure,
		editRule: (ruleId: string, patch: RuleListPatch) => update.mutate({ ruleId, patch }),
		editError: update.error,
		ruleHref,
		openRule: (ruleId: string) => void navigate(ruleHref(ruleId)),
	}
}
